import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addRoomItem,
  addRoomLine,
  buildRoom,
  connectedLines,
  cornerAngle,
  DEFAULT_WALL_HEIGHT,
  emptyRoomPlan,
  flipItemReference,
  flipRoomWall,
  formatMm,
  lineGeom,
  removeRoomLine,
  ROOM_ITEM_LABEL,
  roomDrawTarget,
  setCornerAngle,
  setCornerHeight,
  setLineDirection,
  setLineLength,
  snapRoomPoint,
  visualAngle,
  type ProjectDocument,
  type RoomItemKind,
  type RoomPlan,
  type RoomSnapOptions,
} from '@scs/domain-engine';
import { MmInput } from '../lib/ui';
import { evalMm } from '../lib/mmexpr';
import type { EditorStore } from './store';
import { cornerLabel, itemLabel, lineLabel } from './roomLabels';

type Exec = EditorStore['exec'];
type Tool = 'select' | 'wall' | 'guide' | RoomItemKind;
type Sel = { kind: 'line' | 'item' | 'point'; id: string } | null;

interface SnapSettings {
  on: boolean;
  points: boolean;
  lines: boolean;
  grid: boolean;
  gridMm: number;
  lenOn: boolean;
  lenMm: number;
  angOn: boolean;
  angDeg: number;
}
const SNAP_DEFAULT: SnapSettings = { on: true, points: true, lines: true, grid: false, gridMm: 100, lenOn: true, lenMm: 10, angOn: true, angDeg: 15 };
const SNAP_KEY = 'scs.roomSnap';
function loadSnap(): SnapSettings {
  try {
    return { ...SNAP_DEFAULT, ...(JSON.parse(localStorage.getItem(SNAP_KEY) ?? '{}') as Partial<SnapSettings>) };
  } catch {
    return SNAP_DEFAULT;
  }
}

const TOOLS: { t: Tool; label: string; title: string }[] = [
  { t: 'select', label: '↖ Izaberi', title: 'Izbor linija, tačaka (uglova) i elemenata; povlačenje tačaka' },
  { t: 'wall', label: '▬ Zid', title: 'Crtanje zidova: klik po klik; dužina i ugao se mogu ukucati' },
  { t: 'guide', label: '┄ Vodilja', title: 'Pomoćne linije (ne grade se) — snap ih vidi kao beskonačne' },
  { t: 'window', label: '▭ Prozor', title: 'Kliknite zid' },
  { t: 'door', label: '◜ Vrata', title: 'Kliknite zid' },
  { t: 'socket', label: '⏚ Utičnica', title: 'Kliknite zid' },
  { t: 'drain', label: '◎ Odvod', title: 'Kliknite zid' },
  { t: 'water', label: '💧 Dovod vode', title: 'Kliknite zid' },
];

const fmt = (um: number) => formatMm(Math.round(um));
const PICK_PX = 8;

/**
 * Room editor ("Prostorija"): a clean floor-plan screen. Walls and guides are drawn click by click on
 * the grid; while drawing the length and the angle to the previous line can be typed (Enter). Snap
 * (points, lines / guides, grid) and length / angle increments are switchable. Selected lines, corners
 * and items are edited on the right; corner heights slope the walls. Every change is one undo step.
 */
export function RoomEditor({ doc, exec, readOnly, onClose }: { doc: ProjectDocument; exec: Exec; readOnly: boolean; onClose(): void }) {
  const plan = doc.roomPlan ?? emptyRoomPlan();
  const svgRef = useRef<SVGSVGElement>(null);
  const lenRef = useRef<HTMLInputElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [view, setView] = useState(() => fitView(plan, 800, 600));
  const [tool, setTool] = useState<Tool>(plan.lines.length ? 'select' : 'wall');
  const [snap, setSnapState] = useState<SnapSettings>(loadSnap);
  const [cursor, setCursor] = useState<{ x: number; z: number } | null>(null);
  const [draw, setDraw] = useState<{ start: { x: number; z: number; id?: string }; first?: string; prev: string | null } | null>(null);
  const [lenText, setLenText] = useState('');
  const [angText, setAngText] = useState('');
  const [sel, setSel] = useState<Sel>(null);
  const [drag, setDrag] = useState<{ id: string; x: number; z: number; moved: boolean } | null>(null);
  const pan = useRef<{ x: number; y: number; cx: number; cz: number; moved: boolean; button: number } | null>(null);
  const [space, setSpace] = useState(false);
  const [askHeight, setAskHeight] = useState(false);
  const [heightText, setHeightText] = useState('2600');

  const setSnap = (s: Partial<SnapSettings>) =>
    setSnapState((cur) => {
      const next = { ...cur, ...s };
      try {
        localStorage.setItem(SNAP_KEY, JSON.stringify(next));
      } catch {
        /* per-viewer convenience only */
      }
      return next;
    });

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // First layout: fit the plan to the real size of the screen.
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || size.w === 800) return;
    fitted.current = true;
    setView(fitView(plan, size.w, size.h));
  }, [size, plan]);

  // A dragged point is shown where it is going.
  const shown: RoomPlan = useMemo(() => (drag ? { ...plan, points: plan.points.map((p) => (p.id === drag.id ? { ...p, x: drag.x, z: drag.z } : p)) } : plan), [plan, drag]);
  const built = useMemo(() => buildRoom(shown), [shown]);

  const toScreen = (x: number, z: number) => ({ x: size.w / 2 + (x - view.cx) * view.s, y: size.h / 2 + (z - view.cz) * view.s });
  const toPlan = (sx: number, sy: number) => ({ x: view.cx + (sx - size.w / 2) / view.s, z: view.cz + (sy - size.h / 2) / view.s });
  const tol = PICK_PX / view.s;
  const snapOpts = (ignorePoint?: string): RoomSnapOptions | null =>
    snap.on ? { tolerance: (PICK_PX * 1.5) / view.s, points: snap.points, lines: snap.lines, grid: snap.grid ? snap.gridMm * 1000 : null, ...(ignorePoint ? { ignorePoint } : {}) } : null;

  const typedLen = lenText.trim() ? evalMm(lenText) : null;
  const typedAng = angText.trim() ? Number(angText.replace(',', '.')) : null;
  const target =
    draw && cursor
      ? roomDrawTarget(plan, {
          start: draw.start,
          cursor,
          prevLineId: draw.prev,
          length: typedLen !== null && typedLen > 0 ? typedLen * 1000 : null,
          angle: typedAng !== null && Number.isFinite(typedAng) ? typedAng : null,
          lengthStep: snap.lenOn ? snap.lenMm * 1000 : null,
          angleStep: snap.angOn ? snap.angDeg : null,
          snap: snapOpts(),
        })
      : null;
  const hover = !draw && cursor && (tool === 'wall' || tool === 'guide') ? (snapOpts() ? snapRoomPoint(plan, cursor.x, cursor.z, snapOpts()!) : { ...cursor, kind: null }) : null;

  const edit = (label: string, fn: (p: RoomPlan) => void) =>
    exec(label, (d) => {
      d.roomPlan ??= emptyRoomPlan();
      fn(d.roomPlan);
    });

  const commitDraw = () => {
    if (readOnly || !draw || !target || target.length <= 500) return;
    const kind = tool === 'guide' ? 'guide' : 'wall';
    let made: { id: string; a: string; b: string } | null = null;
    edit(kind === 'wall' ? 'Novi zid' : 'Nova vodilja', (p) => {
      const l = addRoomLine(p, draw.start, { x: target.x, z: target.z, ...(target.pointId ? { id: target.pointId } : {}) }, kind);
      if (l) made = { id: l.id, a: l.a, b: l.b };
    });
    setLenText('');
    setAngText('');
    const m = made as { id: string; a: string; b: string } | null;
    if (!m) return;
    const first = draw.first ?? m.a;
    // Closing onto the first point of the chain ends it; otherwise the next line starts here.
    if (m.b === first) setDraw(null);
    else setDraw({ start: { x: target.x, z: target.z, id: m.b }, first, prev: m.id });
    lenRef.current?.focus();
  };

  // ---------- picking ----------
  const pickPoint = (x: number, z: number) => {
    let best: { id: string; d: number } | null = null;
    for (const p of shown.points) {
      const d = Math.hypot(p.x - x, p.z - z);
      if (d <= tol && (!best || d < best.d)) best = { id: p.id, d };
    }
    return best?.id ?? null;
  };
  const pickLine = (x: number, z: number, wallsOnly = false) => {
    let best: { id: string; d: number; u: number } | null = null;
    for (const l of shown.lines) {
      if (wallsOnly && l.kind !== 'wall') continue;
      const g = lineGeom(shown, l);
      if (!g || !g.length) continue;
      const u = Math.max(0, Math.min(g.length, (x - g.a.x) * g.ux + (z - g.a.z) * g.uz));
      const d = Math.hypot(g.a.x + g.ux * u - x, g.a.z + g.uz * u - z);
      if (d <= Math.max(tol, wallsOnly ? plan.wallThickness + tol : 0) && (!best || d < best.d)) best = { id: l.id, d, u };
    }
    return best;
  };
  const pickItem = (x: number, z: number) => {
    for (const it of built.items) {
      const w = it.wall;
      const u = (x - w.ax) * w.ux + (z - w.az) * w.uz;
      const v = (x - w.ax) * w.nx + (z - w.az) * w.nz;
      const pad = tol;
      if (u >= it.u0 - pad && u <= it.u1 + pad && v >= -pad * 2 && v <= w.thickness + pad) return it.item.id;
    }
    return null;
  };

  // ---------- pointer ----------
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = svgRef.current!.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    if (e.button === 1 || e.button === 2 || (e.button === 0 && space)) {
      pan.current = { x: sx, y: sy, cx: view.cx, cz: view.cz, moved: false, button: e.button };
      svgRef.current!.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const p = toPlan(sx, sy);
    if (tool === 'wall' || tool === 'guide') {
      if (readOnly) return;
      if (!draw) {
        const s = snapOpts() ? snapRoomPoint(plan, p.x, p.z, snapOpts()!) : { x: Math.round(p.x), z: Math.round(p.z), kind: null };
        setDraw({ start: { x: s.x, z: s.z, ...('pointId' in s && s.pointId ? { id: s.pointId } : {}) }, first: 'pointId' in s ? s.pointId : undefined, prev: null });
        setTimeout(() => lenRef.current?.focus(), 0);
      } else commitDraw();
      return;
    }
    if (tool === 'select') {
      const it = pickItem(p.x, p.z);
      if (it) return setSel({ kind: 'item', id: it });
      const pt = pickPoint(p.x, p.z);
      if (pt) {
        setSel({ kind: 'point', id: pt });
        if (!readOnly) {
          const q = plan.points.find((x) => x.id === pt)!;
          setDrag({ id: pt, x: q.x, z: q.z, moved: false });
          svgRef.current!.setPointerCapture(e.pointerId);
        }
        return;
      }
      const l = pickLine(p.x, p.z);
      setSel(l ? { kind: 'line', id: l.id } : null);
      return;
    }
    // Items: click a wall.
    if (readOnly) return;
    const l = pickLine(p.x, p.z, true);
    if (!l) return;
    let id = '';
    edit(`Novi element: ${ROOM_ITEM_LABEL[tool]}`, (pl) => {
      id = addRoomItem(pl, tool, l.id, l.u)?.id ?? '';
    });
    if (id) {
      setSel({ kind: 'item', id });
      setTool('select');
    }
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = svgRef.current!.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    if (pan.current) {
      const dx = sx - pan.current.x;
      const dy = sy - pan.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) pan.current.moved = true;
      setView((v) => ({ ...v, cx: pan.current!.cx - dx / v.s, cz: pan.current!.cz - dy / v.s }));
      return;
    }
    const p = toPlan(sx, sy);
    setCursor(p);
    if (drag) {
      const s = snapOpts(drag.id) ? snapRoomPoint(plan, p.x, p.z, snapOpts(drag.id)!) : { x: Math.round(p.x), z: Math.round(p.z) };
      setDrag({ ...drag, x: s.x, z: s.z, moved: true });
    }
  };
  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    if (pan.current) {
      const wasRight = pan.current.button === 2 && !pan.current.moved;
      pan.current = null;
      // A right click without dragging ends the chain.
      if (wasRight) {
        setDraw(null);
        setLenText('');
        setAngText('');
      }
      return;
    }
    if (drag) {
      const d = drag;
      setDrag(null);
      svgRef.current?.releasePointerCapture(e.pointerId);
      if (d.moved)
        edit('Pomjeranje tačke prostorije', (p) => {
          const q = p.points.find((x) => x.id === d.id);
          if (q) {
            q.x = d.x;
            q.z = d.z;
          }
        });
    }
  };
  const onWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    const r = svgRef.current!.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const before = toPlan(sx, sy);
    const s = Math.min(0.02, Math.max(0.00002, view.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    setView({ s, cx: before.x - (sx - size.w / 2) / s, cz: before.z - (sy - size.h / 2) / s });
  };

  // ---------- keys ----------
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const inInput = t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA';
      if (e.key === ' ' && !inInput) {
        setSpace(true);
        e.preventDefault();
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (lenText || angText) {
          setLenText('');
          setAngText('');
        } else if (draw) setDraw(null);
        else if (sel) setSel(null);
        else if (tool !== 'select') setTool('select');
        return;
      }
      if (inInput) return;
      // Typing a number while drawing goes into the length field.
      if (draw && /^[0-9.,]$/.test(e.key)) {
        lenRef.current?.focus();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && sel && !readOnly) {
        e.preventDefault();
        e.stopPropagation();
        removeSel();
        return;
      }
      const k = e.key.toLowerCase();
      if (!e.ctrlKey && !e.metaKey) {
        if (k === 'w') setTool('wall');
        else if (k === 'g') setTool('guide');
        else if (k === 'v') setTool('select');
        else if (k === 's') setSnap({ on: !snap.on });
      }
      if (!e.ctrlKey && !e.metaKey && !e.altKey) e.stopPropagation();
    };
    const up = (e: KeyboardEvent) => e.key === ' ' && setSpace(false);
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up, true);
    return () => {
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up, true);
    };
  });
  useEffect(() => {
    if (tool !== 'wall' && tool !== 'guide') setDraw(null);
  }, [tool]);

  const removeSel = () => {
    if (!sel) return;
    if (sel.kind === 'line') edit('Brisanje linije prostorije', (p) => removeRoomLine(p, sel.id));
    else if (sel.kind === 'item') edit('Brisanje elementa prostorije', (p) => (p.items = p.items.filter((i) => i.id !== sel.id)));
    else
      edit('Brisanje tačke prostorije', (p) => {
        for (const l of p.lines.filter((x) => x.a === sel.id || x.b === sel.id)) removeRoomLine(p, l.id);
      });
    setSel(null);
  };

  const finish = () => {
    if (plan.wallHeight === null && plan.lines.some((l) => l.kind === 'wall')) return setAskHeight(true);
    onClose();
  };

  // ---------- drawing ----------
  const grid = gridLines(view, size);
  const S = (x: number, z: number) => toScreen(x, z);
  const selLine = sel?.kind === 'line' ? plan.lines.find((l) => l.id === sel.id) : undefined;
  const selItem = sel?.kind === 'item' ? plan.items.find((i) => i.id === sel.id) : undefined;
  const selPoint = sel?.kind === 'point' ? plan.points.find((p) => p.id === sel.id) : undefined;

  return (
    <div className="room-editor" role="dialog" aria-label="Prostorija">
      <div className="room-toolbar">
        <strong>Prostorija</strong>
        <span className="sep" />
        {TOOLS.map((x) => (
          <button key={x.t} className={`btn sm ${tool === x.t ? 'on' : ''}`} title={x.title} disabled={readOnly && x.t !== 'select'} onClick={() => setTool(x.t)}>
            {x.label}
          </button>
        ))}
        {sel?.kind === 'line' && plan.lines.find((l) => l.id === sel.id)?.kind === 'wall' && (
          <>
            <span className="sep" />
            <button className="btn sm" disabled={readOnly} title="Debljina zida ide na drugu stranu linije — unutrašnja (sobna) strana postaje vanjska" onClick={() => edit('Okretanje zida', (p) => flipRoomWall(p, sel.id))}>
              ⇄ Okreni zid
            </button>
          </>
        )}
        <span className="sep" />
        <button className={`btn sm ${snap.on ? 'on' : ''}`} title="Snap uključen / isključen (S)" onClick={() => setSnap({ on: !snap.on })}>
          🧲 Snap
        </button>
        <label className="opt-row" title="Na postojeće tačke (krajeve linija)">
          <input type="checkbox" disabled={!snap.on} checked={snap.points} onChange={(e) => setSnap({ points: e.target.checked })} /> tačke
        </label>
        <label className="opt-row" title="Na linije i vodilje (vodilje su beskonačne) i na njihova presjecišta">
          <input type="checkbox" disabled={!snap.on} checked={snap.lines} onChange={(e) => setSnap({ lines: e.target.checked })} /> linije / presjeci
        </label>
        <label className="opt-row" title="Na mrežu (kad inkrementi nisu uključeni)">
          <input type="checkbox" disabled={!snap.on} checked={snap.grid} onChange={(e) => setSnap({ grid: e.target.checked })} /> mreža
        </label>
        <span className="sep" />
        <label className="opt-row" title="Dužina linije po koracima">
          <input type="checkbox" checked={snap.lenOn} onChange={(e) => setSnap({ lenOn: e.target.checked })} /> korak dužine
        </label>
        <select className="input sm" value={snap.lenMm} disabled={!snap.lenOn} onChange={(e) => setSnap({ lenMm: Number(e.target.value) })} aria-label="Korak dužine">
          {[1, 5, 10, 50, 100, 500].map((v) => (
            <option key={v} value={v}>
              {v} mm
            </option>
          ))}
        </select>
        <label className="opt-row" title="Ugao po koracima (prema prethodnoj liniji)">
          <input type="checkbox" checked={snap.angOn} onChange={(e) => setSnap({ angOn: e.target.checked })} /> korak ugla
        </label>
        <select className="input sm" value={snap.angDeg} disabled={!snap.angOn} onChange={(e) => setSnap({ angDeg: Number(e.target.value) })} aria-label="Korak ugla">
          {[1, 5, 15, 30, 45, 90].map((v) => (
            <option key={v} value={v}>
              {v}°
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn sm primary" onClick={finish} title="Zatvori tlocrt i prikaži zidove u 3D">
          Gotovo → 3D
        </button>
      </div>
      <div className="room-body">
        <svg
          ref={svgRef}
          className={`room-canvas ${space || pan.current ? 'panning' : ''} tool-${tool}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => setCursor(null)}
          onWheel={onWheel}
          onContextMenu={(e) => e.preventDefault()}
        >
          {grid.minor.map((g, i) => (
            <line key={`m${i}`} {...g} className="rg-minor" />
          ))}
          {grid.major.map((g, i) => (
            <line key={`M${i}`} {...g} className="rg-major" />
          ))}
          {(() => {
            const o = S(0, 0);
            return (
              <g className="rg-origin">
                <line x1={o.x} y1={0} x2={o.x} y2={size.h} />
                <line x1={0} y1={o.y} x2={size.w} y2={o.y} />
              </g>
            );
          })()}
          {built.floor && <polygon className="room-floor" points={built.floor.map((p) => `${S(p.x, p.z).x},${S(p.x, p.z).y}`).join(' ')} />}
          {/* Walls with their thickness (outwards). */}
          {built.walls.map((w) => {
            // Inner face between the points; the outer face mitred at the corners (extA / extB).
            const a0 = { x: w.ax, z: w.az };
            const b0 = { x: w.ax + w.ux * w.length, z: w.az + w.uz * w.length };
            const t = w.thickness;
            const q = [a0, b0, { x: b0.x + w.ux * w.extB + w.nx * t, z: b0.z + w.uz * w.extB + w.nz * t }, { x: a0.x - w.ux * w.extA + w.nx * t, z: a0.z - w.uz * w.extA + w.nz * t }];
            return <polygon key={w.lineId} className="room-wall" points={q.map((p) => `${S(p.x, p.z).x},${S(p.x, p.z).y}`).join(' ')} />;
          })}
          {shown.lines.map((l) => {
            const g = lineGeom(shown, l);
            if (!g) return null;
            const a = S(g.a.x, g.a.z);
            const b = S(g.b.x, g.b.z);
            const isSel = sel?.kind === 'line' && sel.id === l.id;
            const ext = 4000;
            return (
              <g key={l.id}>
                {l.kind === 'guide' && <line className="room-guide-ext" x1={a.x - g.ux * ext} y1={a.y - g.uz * ext} x2={b.x + g.ux * ext} y2={b.y + g.uz * ext} />}
                <line className={`room-line ${l.kind} ${isSel ? 'sel' : ''}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
                <DimLabel a={a} b={b} text={fmt(g.length)} guide={l.kind === 'guide'} />
              </g>
            );
          })}
          {/* Corner angles between neighbouring lines. */}
          {shown.points.map((p) => {
            const ls = shown.lines.filter((l) => l.a === p.id || l.b === p.id);
            if (ls.length !== 2) return null;
            const ang = cornerAngle(shown, ls[0]!.id, ls[1]!.id);
            if (ang === null) return null;
            const s = S(p.x, p.z);
            const dirs = ls.map((l) => {
              const g = lineGeom(shown, l)!;
              const o = l.a === p.id ? g.b : g.a;
              return { x: o.x - p.x, z: o.z - p.z };
            });
            const mx = dirs[0]!.x / Math.hypot(dirs[0]!.x, dirs[0]!.z) + dirs[1]!.x / Math.hypot(dirs[1]!.x, dirs[1]!.z);
            const mz = dirs[0]!.z / Math.hypot(dirs[0]!.x, dirs[0]!.z) + dirs[1]!.z / Math.hypot(dirs[1]!.x, dirs[1]!.z);
            const m = Math.hypot(mx, mz) || 1;
            return (
              <text key={`a${p.id}`} className="room-angle" x={s.x + (mx / m) * 22} y={s.y + (mz / m) * 22 + 4} textAnchor="middle">
                {Math.round(ang * 10) / 10}°
              </text>
            );
          })}
          {built.items.map(({ item, wall: w, u0, u1 }) => {
            const isSel = sel?.kind === 'item' && sel.id === item.id;
            const at = (u: number, v: number) => S(w.ax + w.ux * u + w.nx * v, w.az + w.uz * u + w.nz * v);
            const cls = `room-item ${item.kind} ${isSel ? 'sel' : ''}`;
            if (item.kind === 'window' || item.kind === 'door') {
              const q = [at(u0, 0), at(u1, 0), at(u1, w.thickness), at(u0, w.thickness)];
              const leaf = item.kind === 'door' ? [at(u0, 0), at(u0, -(u1 - u0))] : null;
              return (
                <g key={item.id}>
                  <polygon className={cls} points={q.map((p) => `${p.x},${p.y}`).join(' ')} />
                  {leaf && <line className={cls} x1={leaf[0]!.x} y1={leaf[0]!.y} x2={leaf[1]!.x} y2={leaf[1]!.y} />}
                  {leaf && <path className={`${cls} arc`} d={`M ${leaf[1]!.x} ${leaf[1]!.y} A ${(u1 - u0) * view.s} ${(u1 - u0) * view.s} 0 0 ${w.ux * w.nz - w.uz * w.nx > 0 ? 1 : 0} ${at(u1, 0).x} ${at(u1, 0).y}`} />}
                </g>
              );
            }
            const c = at((u0 + u1) / 2, -Math.max(30000, (u1 - u0) / 2));
            const r = Math.max(5, ((u1 - u0) / 2) * view.s);
            return item.kind === 'socket' ? (
              <rect key={item.id} className={cls} x={c.x - r} y={c.y - r} width={2 * r} height={2 * r} />
            ) : (
              <circle key={item.id} className={cls} cx={c.x} cy={c.y} r={r} />
            );
          })}
          {shown.points.map((p) => {
            const s = S(p.x, p.z);
            const isSel = sel?.kind === 'point' && sel.id === p.id;
            return (
              <g key={p.id}>
                <circle className={`room-point ${isSel ? 'sel' : ''}`} cx={s.x} cy={s.y} r={isSel ? 5 : 3.5} />
                {p.h !== undefined && (
                  <text className="room-height" x={s.x + 8} y={s.y - 8}>
                    h {fmt(p.h)}
                  </text>
                )}
              </g>
            );
          })}
          {/* Line being drawn. */}
          {draw && target && (
            <g>
              <line className={`room-line preview ${tool === 'guide' ? 'guide' : 'wall'}`} {...seg(S(draw.start.x, draw.start.z), S(target.x, target.z))} />
              <text className="room-preview-label" x={S(target.x, target.z).x + 12} y={S(target.x, target.z).y - 12}>
                {fmt(target.length)} mm · {Math.round(target.angle * 10) / 10}°
              </text>
              {target.kind && <SnapMark at={S(target.x, target.z)} kind={target.kind} />}
            </g>
          )}
          {hover?.kind && <SnapMark at={S(hover.x, hover.z)} kind={hover.kind} />}
          {drag && <SnapMark at={S(drag.x, drag.z)} kind="point" />}
        </svg>
        <aside className="room-side">
          {selLine && (
            <LineProps
              plan={plan}
              lineId={selLine.id}
              readOnly={readOnly}
              edit={edit}
              onRemove={removeSel}
            />
          )}
          {selItem && <ItemProps plan={plan} itemId={selItem.id} readOnly={readOnly} edit={edit} onRemove={removeSel} />}
          {selPoint && (
            <div className="prop-group">
              <h3>{plan.lines.some((l) => l.kind === 'wall' && (l.a === selPoint.id || l.b === selPoint.id)) ? cornerLabel(plan, selPoint.id) : `Tačka ${selPoint.id}`}</h3>
              <div className="prop-row">
                <label>X</label>
                <MmInput label="X tačke" value={selPoint.x} min={-100000} max={100000} disabled={readOnly} onCommit={(v) => edit('Pomjeranje tačke prostorije', (p) => (p.points.find((q) => q.id === selPoint.id)!.x = v))} />
              </div>
              <div className="prop-row">
                <label>Z</label>
                <MmInput label="Z tačke" value={selPoint.z} min={-100000} max={100000} disabled={readOnly} onCommit={(v) => edit('Pomjeranje tačke prostorije', (p) => (p.points.find((q) => q.id === selPoint.id)!.z = v))} />
              </div>
              <div className="prop-row">
                <label>Visina vertikale</label>
                <MmInput
                  label="Visina vertikalne ivice"
                  value={selPoint.h ?? plan.wallHeight ?? DEFAULT_WALL_HEIGHT}
                  min={1}
                  max={20000}
                  disabled={readOnly}
                  onCommit={(v) => edit('Visina ugla (kosina)', (p) => setCornerHeight(p, selPoint.id, v))}
                />
              </div>
              {selPoint.h !== undefined && (
                <button className="btn sm" disabled={readOnly} onClick={() => edit('Visina ugla kao zid', (p) => setCornerHeight(p, selPoint.id, null))}>
                  ↺ Kao visina zida
                </button>
              )}
              <p className="faint" style={{ fontSize: 11 }}>Različite visine uglova prave kosinu — gornja ivica zida ide pravo od jednog ugla do drugog.</p>
            </div>
          )}
          <div className="prop-group">
            <h3>Zidovi</h3>
            <div className="prop-row">
              <label>Visina</label>
              {plan.wallHeight === null ? (
                <span className="faint">još nije zadana</span>
              ) : (
                <MmInput label="Visina zidova" value={plan.wallHeight} min={100} max={20000} disabled={readOnly} onCommit={(v) => edit('Visina zidova', (p) => (p.wallHeight = v))} />
              )}
            </div>
            <div className="prop-row">
              <label>Debljina</label>
              <MmInput label="Debljina zidova" value={plan.wallThickness} min={1} max={2000} disabled={readOnly} onCommit={(v) => edit('Debljina zidova', (p) => (p.wallThickness = v))} />
            </div>
            <p className="faint" style={{ fontSize: 11 }}>
              Linija je unutrašnja strana zida; debljina ide prema van. Zidova: {plan.lines.filter((l) => l.kind === 'wall').length}, vodilja: {plan.lines.filter((l) => l.kind === 'guide').length}.
            </p>
          </div>
          {built.issues.length > 0 && (
            <div className="prop-group">
              {built.issues.map((i, n) => (
                <p key={n} className="warn-text" style={{ fontSize: 12 }}>
                  {i.message}
                </p>
              ))}
            </div>
          )}
          <p className="faint" style={{ fontSize: 11, padding: '0 10px' }}>
            Klik crta, desni klik / Esc završava liniju. Kucanje broja ide u dužinu, Tab u ugao, Enter potvrđuje. Točkić: zum, srednje / desno dugme ili razmak + povlačenje: pomjeranje. W zid, G vodilja, V izbor, S snap, Delete briše.
          </p>
        </aside>
      </div>
      <div className="room-status">
        {draw ? (
          <>
            <span>{tool === 'guide' ? 'Vodilja' : 'Zid'}:</span>
            <label>
              Dužina{' '}
              <input
                ref={lenRef}
                className="input sm"
                style={{ width: 90 }}
                value={lenText}
                placeholder={target ? fmt(target.length) : ''}
                onChange={(e) => setLenText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitDraw();
                  e.stopPropagation();
                }}
              />{' '}
              mm
            </label>
            <label>
              Ugao{' '}
              <input
                className="input sm"
                style={{ width: 70 }}
                value={angText}
                placeholder={target ? String(Math.round(target.angle * 10) / 10) : ''}
                onChange={(e) => setAngText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitDraw();
                  e.stopPropagation();
                }}
              />
              ° {draw.prev ? 'prema prethodnoj liniji' : 'od horizontale'}
            </label>
            {lenText && typedLen === null && <span className="warn-text">neispravna dužina</span>}
          </>
        ) : (
          <span className="faint">{tool === 'wall' || tool === 'guide' ? 'Kliknite početnu tačku (postojeću tačku ili bilo gdje na mreži).' : tool === 'select' ? 'Kliknite liniju, ugao (tačku) ili element; tačke se mogu povući.' : `${ROOM_ITEM_LABEL[tool]}: kliknite zid.`}</span>
        )}
        <div style={{ flex: 1 }} />
        {cursor && (
          <span className="mono faint">
            x {fmt(cursor.x)} · z {fmt(cursor.z)}
          </span>
        )}
      </div>
      {askHeight && (
        <div className="modal-backdrop" onClick={() => setAskHeight(false)}>
          <div className="card modal" role="dialog" aria-label="Visina zidova" onClick={(e) => e.stopPropagation()}>
            <h2>Visina zidova</h2>
            <p>Tlocrt je gotov — kolika je visina zidova? (Kosine se kasnije podešavaju visinom pojedinih uglova.)</p>
            <input className="input" autoFocus value={heightText} onChange={(e) => setHeightText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && document.getElementById('room-h-ok')?.click()} /> mm
            <div className="actions">
              <button className="btn" onClick={() => setAskHeight(false)}>
                Nazad na tlocrt
              </button>
              <button
                id="room-h-ok"
                className="btn primary"
                onClick={() => {
                  const v = evalMm(heightText);
                  if (!v || v <= 0) return;
                  edit('Visina zidova', (p) => (p.wallHeight = Math.round(v * 1000)));
                  setAskHeight(false);
                  onClose();
                }}
              >
                Napravi zidove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function LineProps({ plan, lineId, readOnly, edit, onRemove }: { plan: RoomPlan; lineId: string; readOnly: boolean; edit(label: string, fn: (p: RoomPlan) => void): void; onRemove(): void }) {
  const line = plan.lines.find((l) => l.id === lineId)!;
  const g = lineGeom(plan, line)!;
  const near = connectedLines(plan, line);
  return (
    <div className="prop-group">
      <h3>{lineLabel(plan, lineId)}</h3>
      <div className="prop-row">
        <label>Vrsta</label>
        <select className="input" disabled={readOnly} value={line.kind} onChange={(e) => edit('Vrsta linije', (p) => (p.lines.find((l) => l.id === lineId)!.kind = e.target.value as 'wall' | 'guide'))}>
          <option value="wall">Zid</option>
          <option value="guide">Vodilja (pomoćna)</option>
        </select>
      </div>
      <div className="prop-row">
        <label>Dužina</label>
        <MmInput label="Dužina linije" value={Math.round(g.length)} min={1} max={100000} disabled={readOnly} onCommit={(v) => edit('Dužina linije', (p) => setLineLength(p, lineId, v, 'a'))} />
      </div>
      {near.length === 0 && (
        <div className="prop-row">
          <label>Smjer</label>
          <AngleInput value={visualAngle(g.dx, g.dz)} disabled={readOnly} onCommit={(v) => edit('Smjer linije', (p) => setLineDirection(p, lineId, v, 'a'))} />
        </div>
      )}
      {near.map((m) => (
        <div className="prop-row" key={m.id}>
          <label title="Unutrašnji ugao između ove linije i susjedne (ova se okreće oko zajedničke tačke)">Ugao prema: {lineLabel(plan, m.id)}</label>
          <AngleInput value={cornerAngle(plan, lineId, m.id) ?? 0} disabled={readOnly} onCommit={(v) => edit('Ugao između linija', (p) => setCornerAngle(p, lineId, m.id, v))} />
        </div>
      ))}
      <p className="faint" style={{ fontSize: 11 }}>Dužina pomjera kraj linije; susjedne linije prate tu tačku.</p>
      <button className="btn sm ghost danger" disabled={readOnly} onClick={onRemove}>
        Obriši liniju
      </button>
    </div>
  );
}

export function ItemProps({ plan, itemId, readOnly, edit, onRemove }: { plan: RoomPlan; itemId: string; readOnly: boolean; edit(label: string, fn: (p: RoomPlan) => void): void; onRemove(): void }) {
  const it = plan.items.find((i) => i.id === itemId)!;
  const set = (label: string, fn: (i: typeof it) => void) => edit(label, (p) => fn(p.items.find((i) => i.id === itemId)!));
  const name = ROOM_ITEM_LABEL[it.kind];
  return (
    <div className="prop-group">
      <h3>{itemLabel(plan, it)}</h3>
      <div className="prop-row">
        <label>Širina</label>
        <MmInput label={`Širina: ${name}`} value={it.width} min={1} max={20000} disabled={readOnly} onCommit={(v) => set(`Širina: ${name}`, (i) => (i.width = v))} />
      </div>
      <div className="prop-row">
        <label>Visina</label>
        <MmInput label={`Visina: ${name}`} value={it.height} min={1} max={20000} disabled={readOnly} onCommit={(v) => set(`Visina: ${name}`, (i) => (i.height = v))} />
      </div>
      <div className="prop-row">
        <label>{it.kind === 'window' ? 'Parapet (od poda)' : 'Visina od poda'}</label>
        <MmInput label={`Visina od poda: ${name}`} value={it.elevation} min={0} max={20000} disabled={readOnly} onCommit={(v) => set(`Visina od poda: ${name}`, (i) => (i.elevation = v))} />
      </div>
      <div className="prop-row">
        <label>Odmak od</label>
        <select className="input" disabled={readOnly} value={it.from} onChange={() => edit(`Odmak mjeren od drugog kraja: ${name}`, (p) => flipItemReference(p, itemId))}>
          <option value="a">početka zida</option>
          <option value="b">kraja zida</option>
        </select>
      </div>
      <div className="prop-row">
        <label>Odmak</label>
        <MmInput label={`Odmak: ${name}`} value={it.offset} min={-100000} max={100000} disabled={readOnly} onCommit={(v) => set(`Odmak: ${name}`, (i) => (i.offset = v))} />
      </div>
      <p className="faint" style={{ fontSize: 11 }}>Odmak je razmak od izabranog kraja zida do najbliže ivice elementa. Kad se zid produži, element ostaje na istom odmaku od tog kraja.</p>
      <button className="btn sm ghost danger" disabled={readOnly} onClick={onRemove}>
        Obriši
      </button>
    </div>
  );
}

export function AngleInput({ value, onCommit, disabled }: { value: number; onCommit(v: number): void; disabled?: boolean }) {
  const [text, setText] = useState(String(Math.round(value * 100) / 100));
  useEffect(() => setText(String(Math.round(value * 100) / 100)), [value]);
  const commit = () => {
    const v = Number(text.replace(',', '.'));
    if (Number.isFinite(v) && v > 0 && v < 360 && Math.abs(v - value) > 1e-6) onCommit(v);
  };
  return (
    <div className="num">
      <input aria-label="Ugao" value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => (e.key === 'Enter' && commit(), e.stopPropagation())} />
      <span className="unit">°</span>
    </div>
  );
}

function DimLabel({ a, b, text, guide }: { a: { x: number; y: number }; b: { x: number; y: number }; text: string; guide: boolean }) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L = Math.hypot(dx, dy);
  if (L < 30) return null;
  let ang = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (ang > 90) ang -= 180;
  if (ang < -90) ang += 180;
  const nx = -dy / L;
  const ny = dx / L;
  const mx = (a.x + b.x) / 2 + nx * 12;
  const my = (a.y + b.y) / 2 + ny * 12;
  return (
    <text className={`room-dim ${guide ? 'guide' : ''}`} x={mx} y={my} textAnchor="middle" dominantBaseline="middle" transform={`rotate(${ang} ${mx} ${my})`}>
      {text}
    </text>
  );
}

function SnapMark({ at, kind }: { at: { x: number; y: number }; kind: string }) {
  if (kind === 'point') return <rect className="room-snap" x={at.x - 6} y={at.y - 6} width={12} height={12} />;
  if (kind === 'intersection')
    return (
      <g className="room-snap">
        <line x1={at.x - 6} y1={at.y - 6} x2={at.x + 6} y2={at.y + 6} />
        <line x1={at.x - 6} y1={at.y + 6} x2={at.x + 6} y2={at.y - 6} />
      </g>
    );
  if (kind === 'line') return <circle className="room-snap" cx={at.x} cy={at.y} r={6} />;
  return <circle className="room-snap grid" cx={at.x} cy={at.y} r={3} />;
}

const seg = (a: { x: number; y: number }, b: { x: number; y: number }) => ({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });

function fitView(plan: RoomPlan, w: number, h: number): { cx: number; cz: number; s: number } {
  if (!plan.points.length) return { cx: 2500_000, cz: 2000_000, s: Math.min(w / 7_000_000, h / 6_000_000) };
  const xs = plan.points.map((p) => p.x);
  const zs = plan.points.map((p) => p.z);
  const [x0, x1, z0, z1] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
  const s = Math.min((w * 0.8) / Math.max(1_000_000, x1 - x0), (h * 0.8) / Math.max(1_000_000, z1 - z0));
  return { cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, s };
}

function gridLines(view: { cx: number; cz: number; s: number }, size: { w: number; h: number }) {
  const steps = [10, 50, 100, 500, 1000, 5000, 10000].map((v) => v * 1000);
  const minor = steps.find((st) => st * view.s >= 10) ?? steps.at(-1)!;
  const major = minor * (String(minor / 1000).startsWith('5') ? 2 : 5);
  const x0 = view.cx - size.w / 2 / view.s;
  const x1 = view.cx + size.w / 2 / view.s;
  const z0 = view.cz - size.h / 2 / view.s;
  const z1 = view.cz + size.h / 2 / view.s;
  const toX = (x: number) => size.w / 2 + (x - view.cx) * view.s;
  const toY = (z: number) => size.h / 2 + (z - view.cz) * view.s;
  const lines = (step: number) => {
    const out: { x1: number; y1: number; x2: number; y2: number }[] = [];
    for (let x = Math.ceil(x0 / step) * step; x <= x1; x += step) out.push({ x1: toX(x), y1: 0, x2: toX(x), y2: size.h });
    for (let z = Math.ceil(z0 / step) * step; z <= z1; z += step) out.push({ x1: 0, y1: toY(z), x2: size.w, y2: toY(z) });
    return out.slice(0, 600);
  };
  return { minor: lines(minor), major: lines(major) };
}
